<script setup lang="ts">
import { computed, markRaw, ref, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import PlaylistUsageTracksInspectorContent from '@/components/PlaylistUsageTracksInspectorContent.vue';
import SimpleSortableTable, { type SimpleTableColumn } from '@/components/SimpleSortableTable.vue';
import TrackBanEverywhereDialog from '@/components/TrackBanEverywhereDialog.vue';
import { Button } from '@/components/ui/button';
import {
  banLiveTrackEverywhere,
  liveTrackUsagesQueryKey,
  TRACK_BAN_LIST_REFRESH_OPERATION_TYPE,
  useLiveTrackUsages,
  type LiveTrackUsage,
  TRACK_BAN_EVERYWHERE_OPERATION_TYPE,
  type TrackBanEverywhereResult,
} from '@/composables/api/useLiveTrackUsagesApi';
import { useScopedPendingAsyncOperation } from '@/composables/api/useAsyncOperationsApi';
import { useAsyncOperationSubscription } from '@/composables/useAsyncOperationSubscription';
import { usePermissions } from '@/composables/usePermissions';
import { CUSTOM_LIST_STATUS } from '@/constants/lookupFilterOptions';
import { useInspectorStore } from '@/stores/inspectorStore';
import { formatCount } from '@/utils/formatCount';
import { usageLocationZoneLabel, usageOwnershipLabel } from '@/utils/liveTrackRow';

/**
 * Every bucket and custom list a track is on, from the nightly playlist_tracks table, and the
 * one button that bans the track in all of those zones.
 * Spec: tabasco docs/specs/track-inspector-playlist-lists.md.
 */
const props = defineProps<{
  liveTrackId: number;
  /** For the confirm dialog's title. */
  trackName?: string | null;
}>();

type BadgeTone = 'warn' | 'danger' | 'muted';

const BADGE_CLASSES: Record<BadgeTone, string> = {
  warn: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  danger: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200',
  muted: 'bg-muted text-foreground/70',
};

const inspectorStore = useInspectorStore();
const queryClient = useQueryClient();
const { can } = usePermissions();
const usagesQuery = useLiveTrackUsages(() => props.liveTrackId);

const usages = computed<LiveTrackUsage[]>(() => usagesQuery.data.value?.data ?? []);
const total = computed(() => usagesQuery.data.value?.meta.total ?? 0);
const truncated = computed(() => usagesQuery.data.value?.meta.truncated ?? false);
const banPlan = computed(() => usagesQuery.data.value?.meta.ban_everywhere ?? null);

function sourceTypeLabel(usage: LiveTrackUsage): string {
  return usage.source_type === 'bucket' ? 'Bucket' : 'Custom List';
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

const usageColumns: SimpleTableColumn[] = [
  // The zone column carries two zone lines, so it takes the room the others leave.
  { key: 'location_zone', label: 'Zone' },
  { key: 'playlist', label: 'List', width: 170 },
  { key: 'track_count', label: 'Tracks', width: 64 },
];

/** Sort value for each column; the cells render richer content through the slot. */
function usageCellValue(usage: LiveTrackUsage, columnKey: string): string | number | null {
  switch (columnKey) {
    case 'location_zone':
      return usageLocationZoneLabel(usage);
    case 'playlist':
      return usage.source_name || null;
    case 'track_count':
      return usage.track_count;
    default:
      return null;
  }
}

function usageRowKey(usage: LiveTrackUsage): string {
  return `${usage.source_type}-${usage.source_id}`;
}

function zoneLineRow(usage: LiveTrackUsage): Record<string, unknown> {
  return { location_zone: usage.location_zone, location: usage.location };
}

function masterZoneLineRow(usage: LiveTrackUsage): Record<string, unknown> | null {
  const master = usage.master_location_zone;
  return master ? { location_zone: master, location: master.location } : null;
}

function usageBadges(usage: LiveTrackUsage): { label: string; tone: BadgeTone }[] {
  const badges: { label: string; tone: BadgeTone }[] = [];
  if (usage.location_zone_custom_playlist_status_id === CUSTOM_LIST_STATUS.DISABLED) {
    badges.push({ label: 'Disabled', tone: 'warn' });
  }
  if (usage.is_archived) {
    badges.push({ label: 'Archived', tone: 'muted' });
  }
  if (usage.is_banned) {
    badges.push({ label: 'Banned', tone: 'danger' });
  }
  return badges;
}

/**
 * Drill into the list itself: push so the back arrow returns to the usage table.
 * Playlist names repeat across zones, so the owning client / location — zone rides
 * along as the inspector subtitle and the content header (shared helpers in
 * liveTrackRow.ts — same composition everywhere).
 */
function openListTracks(usage: LiveTrackUsage): void {
  const ownershipLabel = usageOwnershipLabel(usage) ?? undefined;

  inspectorStore.push(
    usage.source_name || sourceTypeLabel(usage),
    markRaw(PlaylistUsageTracksInspectorContent),
    { sourceType: usage.source_type, sourceId: usage.source_id, ownershipLabel },
    { subtitle: ownershipLabel },
  );
}

// ── Ban everywhere ──────────────────────────────────────────────────────────
//
// The ban runs in a queued job: the busiest tracks are on more than 1,500 zones. The panel
// follows two async operations, the ban and then the list refresh it starts, each by its
// broadcast with the scoped pending lookup as the fallback.

const showBanDialog = ref(false);
const banResult = ref<TrackBanEverywhereResult | null>(null);
/** The ban job. Set while it runs. */
const banOperationId = ref<string | null>(null);
const banFailureMessage = ref<string | null>(null);
/** The list refresh that follows a ban. Set while it runs. */
const refreshOperationId = ref<string | null>(null);
const refreshFailed = ref(false);
const isBanning = computed(() => banOperationId.value !== null);
const isRefreshing = computed(() => refreshOperationId.value !== null);
const operationScope = computed(() => `LIVE-TRACK-${props.liveTrackId}`);

function refetchUsages(): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: liveTrackUsagesQueryKey(props.liveTrackId) });
}

function finishBan(finishedOperationId: string, status: string | undefined, meta: Record<string, unknown> | undefined): void {
  if (banOperationId.value !== finishedOperationId) {
    return;
  }
  banOperationId.value = null;
  if (status === 'succeeded' && meta) {
    const result = meta as unknown as TrackBanEverywhereResult;
    banResult.value = result;
    refreshOperationId.value = result.refresh_operation_id;
  } else {
    banFailureMessage.value =
      typeof meta?.message === 'string' ? meta.message : "Couldn't finish the ban. Press it again to finish the zones it missed.";
  }
  // The ban flags change now; the rows themselves drop off when the refresh finishes.
  void refetchUsages();
}

/**
 * Stay in "Updating the lists…" until the fresh rows are back. Clearing it first shows the
 * old rows, and the ban button, for as long as the refetch takes.
 */
async function finishRefresh(status: string | undefined): Promise<void> {
  const finishedOperationId = refreshOperationId.value;
  if (finishedOperationId === null) {
    return;
  }
  await refetchUsages();
  if (refreshOperationId.value === finishedOperationId) {
    refreshOperationId.value = null;
    refreshFailed.value = status === 'failed';
  }
}

useAsyncOperationSubscription(banOperationId, {
  onFinished: (payload) => finishBan(payload.operation_id, payload.status, payload.meta),
});
useAsyncOperationSubscription(refreshOperationId, {
  onFinished: (payload) => void finishRefresh(payload.status),
});

// Always asked once on open, so a ban started earlier (another tab, a closed inspector) shows as
// running. It only keeps polling while the answer is "pending".
const pendingBan = useScopedPendingAsyncOperation(TRACK_BAN_EVERYWHERE_OPERATION_TYPE, operationScope, true);
watch(
  () => pendingBan.data.value,
  (pending) => {
    if (!pending) {
      return;
    }
    if (!pending.operation_id) {
      // The finished operation's record expires 30 seconds after it completes. Seen after
      // that (a missed broadcast, a background tab, a reopen), the lookup only says "nothing
      // pending". The ban is over, so stop waiting and show the rows as they are now.
      if (!pending.pending && banOperationId.value !== null) {
        banOperationId.value = null;
        void refetchUsages();
      }
      return;
    }
    if (pending.pending && banOperationId.value === null && banResult.value === null) {
      banOperationId.value = pending.operation_id;
    } else if (!pending.pending) {
      finishBan(pending.operation_id, pending.status, pending.meta);
    }
  },
  // A cached answer from an earlier open is already there at mount, and is no change to watch.
  { immediate: true },
);

const pendingRefresh = useScopedPendingAsyncOperation(
  TRACK_BAN_LIST_REFRESH_OPERATION_TYPE,
  operationScope,
  isRefreshing,
);
watch(
  () => pendingRefresh.data.value,
  (pending) => {
    if (isRefreshing.value && pending?.pending === false) {
      void finishRefresh(pending.status);
    }
  },
);

const banMutation = useMutation({
  // The track rides along as the variable: a POST still in flight when the panel is handed
  // another track answers for the track it was sent for, and must not land on the next one.
  // reset() in the track watcher does not cancel it.
  mutationFn: (liveTrackId: number) => banLiveTrackEverywhere(liveTrackId),
  onSuccess: (operationId, liveTrackId) => {
    if (liveTrackId !== props.liveTrackId) {
      return;
    }
    showBanDialog.value = false;
    banResult.value = null;
    banFailureMessage.value = null;
    refreshFailed.value = false;
    banOperationId.value = operationId;
    // A job that already finished (a sync queue, or a quick one) broadcast before this
    // subscribed; the pending lookup finds its answer.
    void pendingBan.refetch();
  },
  onError: (_error, liveTrackId) => {
    if (liveTrackId === props.liveTrackId) {
      showBanDialog.value = false;
    }
  },
});

// One mounted panel can be handed another track. What this panel did for the last one must
// not show for the next.
watch(
  () => props.liveTrackId,
  () => {
    showBanDialog.value = false;
    banResult.value = null;
    banOperationId.value = null;
    banFailureMessage.value = null;
    refreshOperationId.value = null;
    refreshFailed.value = false;
    banMutation.reset();
  },
);

const canBan = computed(
  () =>
    can('create', 'banned_track') &&
    banPlan.value !== null &&
    banPlan.value.zones > 0 &&
    !isBanning.value &&
    !isRefreshing.value &&
    !banMutation.isPending.value,
);

const banButtonLabel = computed(() => {
  const plan = banPlan.value;
  if (!plan) {
    return '';
  }
  const zones = plan.zones === 1 ? '1 zone' : `all ${formatCount(plan.zones)} zones`;
  return plan.zones_to_ban > 0 ? `Ban in ${zones}` : `Remove from ${zones}`;
});

/** The result, in sentences: what happened, who was told, what failed. */
const banResultLines = computed(() => {
  const result = banResult.value;
  if (!result) {
    return null;
  }
  const { banned, already_banned: already, inherits_master: inherits, failed } = result.zone_status_counts;
  const zoneParts = [
    banned > 0 ? plural(banned, 'new ban', 'new bans') : null,
    already > 0 ? `${plural(already, 'zone', 'zones')} already banned it` : null,
    inherits > 0 ? `${plural(inherits, 'zone inherits', 'zones inherit')} its master's ban` : null,
  ].filter((part): part is string => part !== null);

  const skippedOfKind = (kind: string) =>
    result.players.skipped.filter((skip) => skip.kind === kind).reduce((sum, skip) => sum + skip.count, 0);
  const oldVersion = skippedOfKind('old_version');
  const playerType = skippedOfKind('player_type');
  const other = skippedOfKind('other');
  const sentences = [
    zoneParts.length > 0 ? `${zoneParts.join(', ')}.` : null,
    result.players.pushed > 0 ? `${plural(result.players.pushed, 'player was', 'players were')} told.` : null,
    result.players.queued > 0 ? `Commands for ${plural(result.players.queued, 'player are', 'players are')} queued.` : null,
    oldVersion > 0
      ? `${plural(oldVersion, 'player runs', 'players run')} an older version and will pick the ban up on the daily refresh.`
      : null,
    playerType > 0
      ? `${plural(playerType, "player doesn't", "players don't")} take commands; the zone ban keeps the track off them.`
      : null,
    other > 0 ? `${plural(other, 'player was', 'players were')} not found.` : null,
  ].filter((sentence): sentence is string => sentence !== null);

  const zones = result.summary.zones;
  const unnamedFailures = failed - result.failed_zones.length;

  return {
    headline:
      failed > 0
        ? `Done in ${formatCount(zones - failed)} of ${plural(zones, 'zone', 'zones')}.`
        : `Done in ${plural(zones, 'zone', 'zones')}.`,
    detail: sentences.join(' '),
    failures: [
      ...result.failed_zones.map(
        (zone) => `${zone.location_zone_name ?? `Zone #${zone.location_zone_id}`}: ${zone.message ?? 'Failed.'}`,
      ),
      ...(unnamedFailures > 0 ? [`${plural(unnamedFailures, 'more zone', 'more zones')}.`] : []),
    ],
    notNotifiedCount: result.players_not_notified_zone_count,
  };
});
</script>

<template>
  <div class="flex flex-col gap-3 p-4" data-testid="live-track-usage-inspector">
    <div v-if="usagesQuery.isLoading.value" class="text-muted-foreground text-sm">
      Loading usages…
    </div>
    <div v-else-if="usagesQuery.isError.value" class="text-destructive text-sm">
      Failed to load track usages.
    </div>
    <template v-else>
      <div
        v-if="banResultLines"
        class="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
        data-testid="track-ban-result"
      >
        <b>{{ banResultLines.headline }}</b> {{ banResultLines.detail }}
        <p v-for="failure in banResultLines.failures" :key="failure" class="text-destructive mt-1">
          Failed: {{ failure }}
        </p>
        <p v-if="banResultLines.notNotifiedCount > 0" class="text-destructive mt-1">
          {{ plural(banResultLines.notNotifiedCount, 'zone is', 'zones are') }} banned, but the players
          were not told. The error has been reported.
        </p>
      </div>
      <div v-if="refreshFailed" class="text-destructive text-sm" data-testid="track-ban-refresh-failed">
        Some lists couldn't be refreshed, so they may still list the track until tonight's refresh.
        The bans are in place. The error has been reported.
      </div>
      <div v-if="banMutation.isError.value && banMutation.variables.value === liveTrackId" class="text-destructive text-sm" data-testid="track-ban-error">
        Couldn't start the ban. Try again.
      </div>
      <div v-if="banFailureMessage" class="text-destructive text-sm" data-testid="track-ban-failed">
        {{ banFailureMessage }}
      </div>

      <div class="flex items-center justify-between gap-3">
        <div class="text-sm">
          <template v-if="isBanning">
            <b>Banning in {{ banPlan ? plural(banPlan.zones, 'zone', 'zones') : 'every zone' }}…</b>
            <div class="text-muted-foreground text-xs">This runs in the background; you can close the inspector</div>
          </template>
          <template v-else-if="isRefreshing">
            <b>Updating the lists…</b>
            <div class="text-muted-foreground text-xs">Rows drop off when the refresh finishes</div>
          </template>
          <template v-else>
            <span class="text-muted-foreground">
              Used in {{ total }} {{ total === 1 ? 'place' : 'places' }}
            </span>
            <span class="text-muted-foreground text-xs"> (as of the latest nightly refresh)</span>
          </template>
        </div>
        <Button
          v-if="canBan"
          variant="destructive"
          size="sm"
          data-testid="track-ban-everywhere-button"
          @click="showBanDialog = true"
        >
          {{ banButtonLabel }}
        </Button>
      </div>
      <div v-if="truncated" class="bg-muted text-muted-foreground rounded px-2 py-1 text-xs">
        Showing the first {{ usages.length }} of {{ total }} usages. The ban covers all of them.
      </div>
      <div v-if="usages.length === 0" class="text-muted-foreground text-sm">
        This track is not on any bucket or custom list.
      </div>
      <SimpleSortableTable
        v-else
        :columns="usageColumns"
        :rows="usages"
        :row-key="usageRowKey"
        storage-key="live-track-usages:columnWidths:v2"
        :get-value="usageCellValue"
        clickable-rows
        @row-click="openListTracks"
      >
        <template #cell="{ row, column }">
          <div v-if="column.key === 'location_zone'" class="min-w-0 text-xs" data-testid="usage-zone-cell">
            <div v-if="row.account_name" class="truncate font-medium" data-testid="usage-account">
              {{ row.account_name }}
            </div>
            <PlaylistContainerZoneLine v-if="row.location_zone" :row="zoneLineRow(row)" />
            <span v-else>—</span>
            <div
              v-if="masterZoneLineRow(row)"
              class="text-muted-foreground mt-0.5 flex min-w-0 items-center gap-1 pl-4"
              data-testid="usage-master-line"
            >
              <span class="shrink-0">Master</span>
              <PlaylistContainerZoneLine :row="masterZoneLineRow(row)!" />
            </div>
            <div
              v-else-if="row.subscribers_count > 0"
              class="text-muted-foreground mt-0.5 pl-4"
              data-testid="usage-master-line"
            >
              Master of {{ plural(row.subscribers_count, 'subscriber zone', 'subscriber zones') }}
            </div>
          </div>
          <div v-else-if="column.key === 'playlist'" class="flex min-w-0 flex-wrap items-center gap-1">
            <span class="font-medium underline-offset-2 hover:underline">{{ row.source_name || '—' }}</span>
            <span class="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[11px] font-medium">
              {{ sourceTypeLabel(row) }}
            </span>
            <span
              v-for="badge in usageBadges(row)"
              :key="badge.label"
              class="rounded px-1.5 py-0.5 text-[11px] font-medium"
              :class="BADGE_CLASSES[badge.tone]"
              :data-testid="`usage-badge-${badge.label.toLowerCase()}`"
            >
              {{ badge.label }}
            </span>
          </div>
          <span v-else-if="column.key === 'track_count'" class="tabular-nums">
            {{ row.track_count === null ? '—' : formatCount(row.track_count) }}
          </span>
        </template>
      </SimpleSortableTable>
    </template>

    <TrackBanEverywhereDialog
      v-if="banPlan"
      v-model:open="showBanDialog"
      :track-name="trackName ?? null"
      :plan="banPlan"
      :pending="banMutation.isPending.value"
      @confirm="banMutation.mutate(liveTrackId)"
    />
  </div>
</template>
